import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface, PaceInsightsService } from '@evorait/pace-logging';
import { LZStringService } from '../l-z-string/l-z-string.service';
import { BaseStorageService } from './base-storage.service';
import { FileDexieService } from './file-dexie.service';
import { from, Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { isDefined } from '../../utilities/is-defined';
import { isErrorWithCode } from '../../utilities/is-error-with-code';

@Injectable()
export class IndexedDbStorageService extends BaseStorageService {
    public constructor(
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
        protected readonly LZString: LZStringService,
        protected readonly paceInsights: PaceInsightsService,
        protected readonly dexieFileStorage: FileDexieService,
    ) {
        super(log, paceInsights, LZString);
    }

    /**
     *
     * Checks if there are any entries using legacy encryption format.
     *
     * This method queries the collection of stored entries and determines whether
     * any entry's content starts with the legacy encryption prefix.
     *
     * This method is only relevant for the transition period from GW encryption
     * keys to Azure encryption keys. Once, all users that have on encryption
     * key on GW also have an encryption key on Azure, it can safely be removed.
     * ~ summer 2026 should be safe
     *
     * @returns {Observable<boolean>} An observable that emits `true` if at least one
     * entry uses legacy encryption, otherwise `false`.
     */
    public hasLegacyEncryption(): Observable<boolean> {
        return this.dexieFileStorage.hasLegacyEncryption();
    }

    /**
     * Upgrades all entries from legacy encryption to the current encryption format.
     *
     * This method performs a read-write transaction on the collection of stored entries.
     * It finds all entries whose content starts with the legacy prefix,
     * re-encrypts their content using the `encryptionMigrator`, and updates the entries
     * in the database.
     *
     * This method is only relevant for the transition period from GW encryption
     * keys to Azure encryption keys. Once, all users that have on encryption
     * key on GW also have an encryption key on Azure, it can safely be removed.
     * ~ summer 2026 should be safe
     *
     * @returns {Observable<boolean>} An observable that emits `true` once all legacy
     * encrypted entries have been successfully migrated.
     */
    public upgradeEncryption(): Observable<boolean> {
        return this.dexieFileStorage.upgradeEncryption();
    }

    protected async checkFile(fileName: string): Promise<boolean> {
        return this.dexieFileStorage
            .getFile(fileName)
            .then(({ file }) => {
                return !!file;
            })
            .catch((error) => {
                // if the file was not cached, ignore the NOT_FOUND_ERR (=> 1)
                const suppressError = isErrorWithCode(error) && error.code === 1;
                if (suppressError) {
                    return false;
                }

                throw error;
            });
    }

    public readAsArrayBufferFromLiveQuery$(
        fileName: string,
    ): Observable<{ buffer: ArrayBuffer; timestamp: number } | undefined> {
        return this.dexieFileStorage
            .selectFile(fileName)
            .pipe(
                mergeMap((result) =>
                    isDefined(result)
                        ? from(result.file.arrayBuffer().then((buffer) => ({ buffer, timestamp: result.timestamp })))
                        : of(undefined),
                ),
            );
    }

    protected async readAsArrayBuffer(fileName: string): Promise<{ buffer: ArrayBuffer; timestamp: number }> {
        return this.dexieFileStorage.getFile(fileName).then(({ file, timestamp }) => {
            return file.arrayBuffer().then((buffer) => ({ buffer, timestamp }));
        });
    }

    protected async removeFile(fileName: string): Promise<void> {
        this.log.debug(`IndexedDbStorageService: removeFile: locking '${fileName}' for saving data`);
        return this.dexieFileStorage.removeFileEntry(fileName).then(() => {});
    }

    protected async writeFile(
        fileName: string,
        content: Uint8Array<ArrayBuffer>,
        timestamp: number = Date.now(),
    ): Promise<void> {
        return this.dexieFileStorage.writeFileEntry(fileName, new Blob([content]), timestamp).then(() => {});
    }
}
